import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import {
  Product,
  ReferralResult,
  ReferralStat,
  TGLaunchParams,
  TgUser,
  UIBundle,
  UserProduct,
} from '../models/Models';
import { useQuery } from '@tanstack/react-query';
import {
  fetchProducts,
  fetchReferralStats,
  fetchUIBundle,
  fetchUserProducts,
} from '../clients/ApiClient';
import {
  init,
  isTMA,
  retrieveLaunchParams,
  swipeBehavior,
  viewport,
} from '@telegram-apps/sdk';
import { mixIdentify, mixTrack } from '../helpers/MixPanelHelper';

interface AppContextType {
  bundle: UIBundle | undefined;
  products: Product[] | undefined;
  userProducts: UserProduct[] | undefined;
  tgInitData: TGLaunchParams | undefined;
  referralResult: ReferralResult | undefined;
  balance: number;
  minersBalance: number;
  referralsBalance: number;
  isLoading: boolean;
  tgUser: TgUser | undefined;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [bundle, setBundle] = useState<UIBundle>();
  const [products, setProducts] = useState<Product[]>();
  const [userProducts, setUserProducts] = useState<UserProduct[]>();
  const [balance, setBalance] = useState<number>(0);
  const [minersBalance, setMinersBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [referralsBalance, setReferralsBalance] = useState<number>(0);
  const [tgInitData, setTgInitData] = useState<TGLaunchParams>();
  const [referralResult, setReferralResult] = useState<ReferralResult>();
  const [tgUser, setTgUser] = useState<TgUser>();

  useEffect(() => {
    isTMA().then((isOpenedInTG) => {
      if (!tgInitData && isOpenedInTG) {
        init();

        if (swipeBehavior.mount.isAvailable()) {
          swipeBehavior.mount();
          if (swipeBehavior.isMounted()) {
            swipeBehavior.disableVertical();
          }
        }

        const initDataRaw = retrieveLaunchParams();
        if (initDataRaw) {
          setTgInitData(
            JSON.parse(JSON.stringify(initDataRaw)) as TGLaunchParams,
          );
        }
      }
    });
  }, []);

  const {
    data: uiBundle,
    error,
    isSuccess,
  } = useQuery({
    queryKey: ['uibundle'], // Unique key for caching
    queryFn: fetchUIBundle,
    refetchInterval: 300000, // 5 minutes (300,000 ms)
    enabled: tgInitData && tgInitData !== undefined,
  });

  // All other queries must wait for UIBundle first, otherwise duplicate members will be created!
  const { data: productsList } = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts,
    refetchInterval: 300000,
    enabled: tgInitData && uiBundle && isSuccess,
  });

  const { data: userProductsList } = useQuery({
    queryKey: ['user_products'],
    queryFn: fetchUserProducts,
    refetchInterval: 300000,
    enabled: tgInitData && uiBundle && isSuccess,
  });

  const { data: referralStatsList } = useQuery({
    queryKey: ['referral_stats'],
    queryFn: fetchReferralStats,
    refetchInterval: 300000,
    enabled: tgInitData && uiBundle && isSuccess,
  });

  useEffect(() => {
    if (tgInitData?.initData.user) {
      setTgUser(tgInitData?.initData.user);

      const user = tgInitData!.initData.user;

      if (user) {
        mixIdentify(tgInitData!.initData.user.id, {
          id: user.id,
          username: user.username,
          firstName: user.firstName,
          lastName: user.lastName,
          isPremium: user.isPremium,
          languageCode: user.languageCode,
          photoUrl: user.photoUrl,
          allowPM: user.allowsWriteToPm,
        });

        mixTrack(`App Load`);
      }
    }
  }, [tgInitData]);

  useEffect(() => {
    if (uiBundle) {
      setBundle(uiBundle);
    }
  }, [uiBundle]);

  useEffect(() => {
    if (productsList) {
      setProducts(productsList);
    }
  }, [productsList]);

  useEffect(() => {
    if (userProductsList) {
      setUserProducts(userProductsList);
    } else {
      setBalance(0);
    }
  }, [userProductsList]);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  }, []);

  useEffect(() => {
    if (referralStatsList) {
      if (referralStatsList && referralStatsList.referralStats) {
        let _total = 0;
        for (const r of referralStatsList.referralStats) {
          _total += r.totalBalance;
        }
        setReferralsBalance(_total);
      }
      setReferralResult(referralStatsList);
    }
  }, [referralStatsList]);

  useEffect(() => {
    if (userProducts && userProducts.length > 0) {
      let _balance = 0;
      for (const up of userProducts) {
        _balance += Number(up.currentBalance);
      }

      setMinersBalance(_balance);
    } else {
      setMinersBalance(0);
    }
  }, [userProducts]);

  useEffect(() => {
    setBalance(minersBalance + referralsBalance);
  }, [minersBalance, referralsBalance]);

  return (
    <AppContext.Provider
      value={{
        bundle,
        products,
        userProducts,
        isLoading,
        balance,
        tgInitData,
        referralResult,
        referralsBalance,
        minersBalance,
        tgUser,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context)
    throw new Error('useAppContext must be used within AppProvider');
  return context;
};
